import objectHash from "object-hash";

import { groupByToMap } from "@entrilia/shared/utilities/arrayHelper";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import type {
  ChangeRequestChangeSet,
  InvestorChangeRequestDetails,
  InvestorPortalRole,
  MemberChangeAction,
  MemberChangeContributor,
  MemberExistingContact,
  MemberFundPermissionChange,
} from "../../../../../api/types/workflowTypes";

export interface MemberCategoryChangeView {
  name: string;
  kind: "Added" | "Removed";
}

export interface MemberFundChange {
  key: string;
  fundName: string;
  oldRole?: InvestorPortalRole;
  newRole?: InvestorPortalRole;
  categoryChanges: MemberCategoryChangeView[];
}

export interface MemberChangeView {
  key: string;
  name: string;
  email: string;
  action: MemberChangeAction;
  fundChanges: MemberFundChange[];
  changeCount: number;
  existingContact?: MemberExistingContact;
}

export interface MemberFundChangeGroup {
  key: string;
  fundNames: string[];
  change: MemberFundChange;
}

const buildFundChange = (permission: MemberFundPermissionChange): MemberFundChange => {
  const categoryChanges = permission.categories.flatMap<MemberCategoryChangeView>((category) =>
    category.change === "Added" || category.change === "Removed" ? [{ name: category.name, kind: category.change }] : []
  );

  return {
    key: permission.fundInvestorId,
    fundName: permission.fundName,
    oldRole: permission.oldRole,
    newRole: permission.newRole,
    categoryChanges,
  };
};

const hasRoleChange = (fundChange: MemberFundChange): boolean => fundChange.oldRole !== fundChange.newRole;

const countFundChanges = (fundChange: MemberFundChange): number =>
  fundChange.categoryChanges.length + (hasRoleChange(fundChange) ? 1 : 0);

// Own key, not the table's: the table groups by role from→to, while a role left unchanged is no change to count.
const countKey = (fundChange: MemberFundChange): string =>
  objectHash({
    roleChanged: hasRoleChange(fundChange),
    newRole: hasRoleChange(fundChange) ? (fundChange.newRole ?? null) : null,
    categories: fundChange.categoryChanges.map((category) => `${category.kind}:${category.name}`).sort(),
  });

// Matches the Investor Portal: the same change made on several funds is one change, not one per fund.
const countMemberChanges = (fundChanges: MemberFundChange[]): number =>
  [...groupByToMap(fundChanges, countKey).values()].reduce((total, changes) => total + countFundChanges(changes[0]), 0);

export const buildMemberChanges = (changeSets: ChangeRequestChangeSet[]): MemberChangeView[] =>
  changeSets.flatMap((changeSet, index) => {
    const details = changeSet.memberDetails;
    if (!details) {
      return [];
    }

    const fundChanges = details.fundPermissions
      .map((permission) => buildFundChange(permission))
      .filter((fundChange) => countFundChanges(fundChange) > 0);

    const requestedName = details.name ?? details.oldName ?? "";

    return [
      {
        key: changeSet.scopeId ?? `member-${index}`,
        name: requestedName,
        email: details.email ?? details.oldEmail ?? "",
        action: details.action,
        fundChanges,
        changeCount: countMemberChanges(fundChanges),
        existingContact: details.existingContact,
      },
    ];
  });

const fundChangeSignature = (fundChange: MemberFundChange): string =>
  objectHash({
    oldRole: fundChange.oldRole,
    newRole: fundChange.newRole,
    categories: fundChange.categoryChanges.map((category) => `${category.kind}:${category.name}`).sort(),
  });

export const groupFundChanges = (fundChanges: MemberFundChange[]): MemberFundChangeGroup[] =>
  [...groupByToMap(fundChanges, fundChangeSignature).values()].map((changes) => ({
    key: changes[0].key,
    fundNames: changes.map((change) => change.fundName),
    change: changes[0],
  }));

export const memberActionOrder: MemberChangeAction[] = ["CreateNew", "UpdateExisting", "Delete"];

export const getMemberGroupHeading = (action: MemberChangeAction, count: number): string => {
  switch (action) {
    case "CreateNew":
      return `Adding new contact (${count})`;
    case "Delete":
      return `Removing contact (${count})`;
    default:
      return `Changing contact access (${count})`;
  }
};

// Reviewer's own fund restrictions hid the rest of this contact's footprint; never hints at what it may contain.
export const getOtherMembershipsLabel = (otherMembershipsCount: number): string =>
  `+${otherMembershipsCount} ${pluralize("membership", otherMembershipsCount)} outside your fund access`;

// Covers what the per-contributor flag cannot: a contributor dropped whole, and pruned change sets.
export const getRestrictedContentMessage = (details: InvestorChangeRequestDetails): string | undefined =>
  details.contentRestrictedByEntityLevelAccess
    ? "Parts of this request are outside your fund access and are not shown."
    : undefined;

// Count-free by design: how much was withheld is itself a fact about funds the reviewer cannot see.
export const getRestrictedEvidenceMessage = (contributor: MemberChangeContributor): string | undefined =>
  contributor.evidenceRestrictedByEntityLevelAccess
    ? "Some supporting evidence is outside your fund access and is not shown."
    : undefined;
